import _ from 'lodash';
import { THEME } from '../../constants/notification';
import { getSelectedApplicationId } from '../../selectors/applications/applications';
import { getSelectedOrganizationId } from '../../selectors/organizations/organizations';
import { applicationsService } from '../../transport';
import i18n from '../../utils/i18n';
import { getSelectedProjectId } from '../projects/projects.selectors';
import { Dispatch, GetState } from '../redux.types';

export const APPLICATIONS_ACTION_NAMES = {
  APPLICATIONS_GET_START: 'APPLICATIONS_GET_START',
  APPLICATIONS_GET_SUCCESS: 'APPLICATIONS_GET_SUCCESS',
  APPLICATIONS_GET_ERROR: 'APPLICATIONS_GET_ERROR',

  APPLICATION_GET_START: 'APPLICATION_GET_START',
  APPLICATION_GET_SUCCESS: 'APPLICATION_GET_SUCCESS',
  APPLICATION_GET_ERROR: 'APPLICATION_GET_ERROR',

  APPLICATION_CREATE_SUCCESS: 'APPLICATION_CREATE_SUCCESS',
  APPLICATION_CREATE_ERROR: 'APPLICATION_CREATE_ERROR',

  APPLICATION_UPDATE_SUCCESS: 'APPLICATION_UPDATE_SUCCESS',
  APPLICATION_UPDATE_ERROR: 'APPLICATION_UPDATE_ERROR',

  APPLICATION_KEY_REGENERATE_SUCCESS: 'APPLICATION_KEY_REGENERATE_SUCCESS',
  APPLICATION_KEY_REGENERATE_ERROR: 'APPLICATION_KEY_REGENERATE_ERROR',

  APPLICATION_DELETE_SUCCESS: 'APPLICATION_DELETE_SUCCESS',
  APPLICATION_DELETE_ERROR: 'APPLICATION_DELETE_ERROR',

  SECURITY_GROUPS_UPDATE_SUCCESS: 'SECURITY_GROUPS_UPDATE_SUCCESS',
  SECURITY_GROUPS_UPDATE_ERROR: 'SECURITY_GROUPS_UPDATE_ERROR',

  WEBHOOK_ADD_SUCCESS: 'WEBHOOK_ADD_SUCCESS',
  WEBHOOK_ADD_ERROR: 'WEBHOOK_ADD_ERROR',

  WEBHOOK_UPDATE_SUCCESS: 'WEBHOOK_UPDATE_SUCCESS',
  WEBHOOK_UPDATE_ERROR: 'WEBHOOK_UPDATE_ERROR',

  WEBHOOK_DELETE_SUCCESS: 'WEBHOOK_DELETE_SUCCESS',
  WEBHOOK_DELETE_ERROR: 'WEBHOOK_DELETE_ERROR',

  WEBHOOK_TOGGLE_ACTIVITY_SUCCESS: 'WEBHOOK_TOGGLE_ACTIVITY_SUCCESS',
  WEBHOOK_TOGGLE_ACTIVITY_ERROR: 'WEBHOOK_TOGGLE_ACTIVITY_ERROR',
};

const applicationCreateSuccess = (application) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_CREATE_SUCCESS,
  payload: application,
});

const applicationCreateError = (reason) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_CREATE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.application.create.error'),
    theme: THEME.ERROR,
  },
});

const applicationUpdateSuccess = (application) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_UPDATE_SUCCESS,
  payload: application,
});

const applicationUpdateError = (reason) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_UPDATE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.application.update.error'),
    theme: THEME.ERROR,
  },
});

const applicationKeyRegenerateSuccess = (application) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_KEY_REGENERATE_SUCCESS,
  payload: application,
});

const applicationKeyRegenerateError = (reason) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_KEY_REGENERATE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.application.key.regenerate.error'),
    theme: THEME.ERROR,
  },
});

const applicationDeleteSuccess = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_DELETE_SUCCESS,
  payload,
});

const applicationDeleteError = (reason, name) => ({
  type: APPLICATIONS_ACTION_NAMES.APPLICATION_DELETE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.application.delete.error', { name }),
    theme: THEME.ERROR,
  },
});

const securityGroupsUpdateSuccess = (application) => ({
  type: APPLICATIONS_ACTION_NAMES.SECURITY_GROUPS_UPDATE_SUCCESS,
  payload: application,
});

const securityGroupsUpdateError = (reason) => ({
  type: APPLICATIONS_ACTION_NAMES.SECURITY_GROUPS_UPDATE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.application.security-groups.update.error'),
    theme: THEME.ERROR,
  },
});

const addWebhookSuccess = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_ADD_SUCCESS,
  payload,
  notification: {
    message: i18n.t('notifications.application.webhook.create.success'),
    theme: THEME.SUCCESS,
  },
});

const addWebhookError = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_ADD_ERROR,
  payload,
  notification: {
    message: i18n.t('notifications.application.webhook.create.error'),
    theme: THEME.ERROR,
  },
});

const webhookUpdateSuccess = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_UPDATE_SUCCESS,
  payload,
  notification: {
    message: i18n.t('notifications.application.webhook.update.success'),
    theme: THEME.SUCCESS,
  },
});

const webhookUpdateError = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_UPDATE_ERROR,
  payload,
  notification: {
    message: i18n.t('notifications.application.webhook.update.error'),
    theme: THEME.ERROR,
  },
});

const webhookDeleteSuccess = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_DELETE_SUCCESS,
  payload,
  notification: {
    message: i18n.t('notifications.application.webhook.delete.success'),
    theme: THEME.SUCCESS,
  },
});

const webhookDeleteError = (reason) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_DELETE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.application.webhook.delete.error'),
    theme: THEME.ERROR,
  },
});

const webhookToggleActivitySuccess = (payload) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_TOGGLE_ACTIVITY_SUCCESS,
  payload,
});

const webhookToggleActivityError = (payload, toggleAction) => ({
  type: APPLICATIONS_ACTION_NAMES.WEBHOOK_TOGGLE_ACTIVITY_ERROR,
  payload,
  notification: {
    message: i18n.t(`notifications.application.webhook.${toggleAction}.error`),
    theme: THEME.ERROR,
  },
});

// APPLICATIONS

export const getApplications =
  ({ organizationId, projectId }) =>
  (dispatch) => {
    return dispatch({
      types: [
        APPLICATIONS_ACTION_NAMES.APPLICATIONS_GET_START,
        APPLICATIONS_ACTION_NAMES.APPLICATIONS_GET_SUCCESS,
        APPLICATIONS_ACTION_NAMES.APPLICATIONS_GET_ERROR,
      ],
      callApi: () =>
        applicationsService.getApplications({ organizationId, projectId }),
    });
  };

export const getApplication =
  ({ organizationId, projectId, applicationId }) =>
  (dispatch: Dispatch) => {
    return dispatch({
      types: [
        APPLICATIONS_ACTION_NAMES.APPLICATION_GET_START,
        APPLICATIONS_ACTION_NAMES.APPLICATION_GET_SUCCESS,
        APPLICATIONS_ACTION_NAMES.APPLICATION_GET_ERROR,
      ],
      callApi: () =>
        applicationsService.getApplication({
          organizationId,
          projectId,
          applicationId,
        }),
    });
  };

export const createApplication =
  (application: { name: string; securityGroups: string[] }) =>
  (dispatch, getState) => {
    const state = getState();
    const params = {
      projectId: getSelectedProjectId(state),
      organizationId: getSelectedOrganizationId(state),
    };

    return applicationsService.createApplication(application, params).then(
      (response) => dispatch(applicationCreateSuccess(response.data.payload)),
      ({ response }) =>
        dispatch(applicationCreateError(_.get(response, 'data'))),
    );
  };

export const updateApplication = (application, params) => (dispatch) => {
  return applicationsService.updateApplication(application, params).then(
    () => dispatch(applicationUpdateSuccess(application)),
    ({ response }) => dispatch(applicationUpdateError(_.get(response, 'data'))),
  );
};

export const deleteApplication =
  ({
    applicationId,
    applicationName,
  }: {
    applicationId: string;
    applicationName: string;
  }) =>
  (dispatch, getState) => {
    const state = getState();
    const params = {
      projectId: getSelectedProjectId(state),
      organizationId: getSelectedOrganizationId(state),
      applicationId,
    };

    return applicationsService.deleteApplication(params).then(
      () => dispatch(applicationDeleteSuccess({ id: params.applicationId })),
      ({ response }) =>
        dispatch(
          applicationDeleteError(_.get(response, 'data'), applicationName),
        ),
    );
  };

export const generateNewSecret = (params) => (dispatch) => {
  return applicationsService.generateNewSecret(params).then(
    (response) =>
      dispatch(applicationKeyRegenerateSuccess(response.data.payload)),
    ({ response }) =>
      dispatch(applicationKeyRegenerateError(_.get(response, 'data'))),
  );
};

// SECURITY GROUPS

export const updateSecurityGroups =
  (securityGroups: string[]) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const organizationId = getSelectedOrganizationId(state);
    const projectId = getSelectedProjectId(state);
    const applicationId = getSelectedApplicationId(state);

    return applicationsService
      .updateSecurityGroups(securityGroups, {
        organizationId,
        projectId,
        applicationId,
      })
      .then(
        () =>
          dispatch(
            securityGroupsUpdateSuccess({ id: applicationId, securityGroups }),
          ),
        ({ response }) =>
          dispatch(securityGroupsUpdateError(_.get(response, 'data'))),
      );
  };

// WEBHOOKS

export const createWebhook = (webhook) => (dispatch, getState) => {
  const state = getState();
  const params = {
    projectId: getSelectedProjectId(state),
    organizationId: getSelectedOrganizationId(state),
    applicationId: getSelectedApplicationId(state),
  };

  return applicationsService.createWebhook(webhook, params).then(
    () =>
      dispatch(
        addWebhookSuccess({ webhook, applicationId: params.applicationId }),
      ),
    ({ response }) =>
      dispatch(addWebhookError(_.get(response, 'data.payload'))),
  );
};

export const updateWebhook = (webhook) => (dispatch, getState) => {
  const state = getState();
  const params = {
    projectId: getSelectedProjectId(state),
    organizationId: getSelectedOrganizationId(state),
    applicationId: getSelectedApplicationId(state),
  };

  return applicationsService.updateWebhook(webhook, params).then(
    () =>
      dispatch(
        webhookUpdateSuccess({
          webhook,
          applicationId: params.applicationId,
        }),
      ),
    ({ response }) =>
      dispatch(webhookUpdateError(_.get(response, 'data.payload'))),
  );
};

export const deleteWebhook =
  ({ url }) =>
  (dispatch, getState) => {
    const state = getState();
    const params = {
      projectId: getSelectedProjectId(state),
      organizationId: getSelectedOrganizationId(state),
      applicationId: getSelectedApplicationId(state),
    };

    return applicationsService.deleteWebhook({ url }, params).then(
      () =>
        dispatch(
          webhookDeleteSuccess({ url, applicationId: params.applicationId }),
        ),
      ({ response }) => {
        dispatch(webhookDeleteError(_.get(response, 'data')));

        return Promise.reject(response);
      },
    );
  };

export const toggleWebhookActivity = (webhook) => (dispatch, getState) => {
  const state = getState();
  const params = {
    projectId: getSelectedProjectId(state),
    organizationId: getSelectedOrganizationId(state),
    applicationId: getSelectedApplicationId(state),
  };

  return applicationsService.toggleWebhookActivity(webhook, params).then(
    (response) => {
      dispatch(
        webhookToggleActivitySuccess({
          applicationId: params.applicationId,
          webhook: {
            ...webhook,
            active: !webhook.active,
          },
        }),
      );

      return response;
    },
    (reason) => {
      const toggleAction = webhook.active ? 'deactivate' : 'activate';

      dispatch(
        webhookToggleActivityError(
          {
            applicationId: params.applicationId,
            webhook,
          },
          toggleAction,
        ),
      );

      return Promise.reject(reason);
    },
  );
};
